/**
 * Custom hooks for Document Cross-Check Validation Rules
 */
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api-client";
import type {
    ValidationRuleResponse,
    ValidationRuleCreate,
    ValidationRuleUpdate,
    ValidationRuleListFilters,
} from "@/types/api/validation-rules";

const VALIDATION_RULES_QUERY_KEYS = {
    all: ["validation-rules"] as const,
    lists: () => [...VALIDATION_RULES_QUERY_KEYS.all, "list"] as const,
    list: (filters?: ValidationRuleListFilters) => [...VALIDATION_RULES_QUERY_KEYS.lists(), filters] as const,
    details: () => [...VALIDATION_RULES_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...VALIDATION_RULES_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook to list validation rules with filters
 */
export function useValidationRules(
    filters?: ValidationRuleListFilters,
    options?: Omit<UseQueryOptions<ValidationRuleResponse[], Error>, "queryKey" | "queryFn">,
) {
    return useQuery({
        queryKey: VALIDATION_RULES_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<ValidationRuleResponse[]> => {
            return await apiGet<ValidationRuleResponse[]>("/validation/rules", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        ...options,
    });
}

/**
 * Hook to get a single validation rule
 */
export function useValidationRule(ruleId: string) {
    return useQuery({
        queryKey: VALIDATION_RULES_QUERY_KEYS.detail(ruleId),
        queryFn: async (): Promise<ValidationRuleResponse> => {
            return await apiGet<ValidationRuleResponse>(`/validation/rules/${ruleId}`);
        },
        enabled: !!ruleId,
    });
}

/**
 * Hook to create a validation rule
 */
export function useCreateValidationRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: ValidationRuleCreate): Promise<ValidationRuleResponse> => {
            return await apiPost<ValidationRuleResponse>("/validation/rules", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: VALIDATION_RULES_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook to update a validation rule
 */
export function useUpdateValidationRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ ruleId, data }: { ruleId: string; data: ValidationRuleUpdate }): Promise<ValidationRuleResponse> => {
            return await apiPut<ValidationRuleResponse>(`/validation/rules/${ruleId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: VALIDATION_RULES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: VALIDATION_RULES_QUERY_KEYS.detail(variables.ruleId) });
        },
    });
}

/**
 * Hook to delete a validation rule
 */
export function useDeleteValidationRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ruleId: string): Promise<void> => {
            return await apiDelete<void>(`/validation/rules/${ruleId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: VALIDATION_RULES_QUERY_KEYS.lists() });
        },
    });
}
